import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router';

import { createApi } from '@/api';
import { ApiProvider } from '@/api/context';

import { PreferencesProvider } from './app/preferences';
import { createRouter } from './app/router';
import { ToastProvider } from './app/toasts';
import './styles/index.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10_000,
      retry: 1,
      refetchOnWindowFocus: true,
    },
    mutations: { retry: 0 },
  },
});

const api = createApi();
const router = createRouter();

/**
 * A view transition the browser skips (the tab went hidden, or another transition started)
 * rejects with an InvalidStateError that React reports as a recoverable error. The page is
 * updated all the same, so it is not worth a console error; everything else is reported.
 */
function onRecoverableError(error: unknown): void {
  if (
    error instanceof DOMException &&
    error.name === 'InvalidStateError' &&
    /transition/i.test(error.message)
  ) {
    return;
  }
  reportError(error);
}

createRoot(document.getElementById('root')!, { onRecoverableError }).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ApiProvider value={api}>
        <PreferencesProvider>
          <ToastProvider>
            <RouterProvider router={router} />
          </ToastProvider>
        </PreferencesProvider>
      </ApiProvider>
    </QueryClientProvider>
  </StrictMode>,
);
